import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { environment } from '@/environments/environment';
import { AuthenticationService } from '@/app/core/authentication/authentication.service';
import { RequestCancellationService } from '@/app/core/http/request-cancellation.service';
import { retryBeforeThrow } from '@/app/core/utils/retry-before-throw';
import { broadcastError } from '@/app/core/toasts/broadcast-toast';

export enum HTTP_WORD {
  GET = 'get',
  PUT = 'put',
  POST = 'post',
  DELETE = 'delete',
}

/**
 * Handles API calls used for filter profiles.
 */
export class MetricsApiService {
  protected baseUrl = '';
  protected headers: HttpHeaders;
  protected shouldAbort: boolean = true;
  protected cancelSubject$: Subject<any> | null = null;

  constructor(
    protected http: HttpClient,
    protected authenticationService: AuthenticationService,
    protected cancellationService: RequestCancellationService
  ) {
    this.baseUrl = environment.metricsApiUrl;
    this.getHttpHeaders().subscribe();
  }

  /**
   * Disables abort for all requests on this instance.
   * Use with clone() for isolation: service.clone().disableRoutingAbort().performHttpRequest(...)
   */
  public disableRoutingAbort(): MetricsApiService {
    this.shouldAbort = false;
    return this;
  }

  /**
   * Sets a custom cancellation subject for manual control.
   */
  public setCancelSubject(sub$: Subject<any>): MetricsApiService {
    this.cancelSubject$ = sub$;
    return this;
  }

  /**
   * Creates a new instance of MetricsApiService with isolated state.
   * @example
   * this.metricsApiService.clone().disableRoutingAbort().performHttpRequest(...)
   */
  public clone(): MetricsApiService {
    return new MetricsApiService(
      this.http,
      this.authenticationService,
      this.cancellationService
    );
  }

  /**
   * Returns cancellation subject based on shouldAbort flag and any custom cancel subject.
   */
  private getCancelSubject(): Subject<any> | undefined {
    return this.cancellationService.getCancellationSubject(
      this.shouldAbort,
      this.cancelSubject$
    );
  }

  /**
   * Returns Http headers with the current access token.
   */
  public getHttpHeaders(): Observable<HttpHeaders> {
    return new Observable<HttpHeaders>((obs) => {
      try {
        if (this.headers) {
          obs.next(this.headers);
          obs.complete();
        } else {
          this.authenticationService.selectedClientId.subscribe((clientId) => {
            // Wait untill a client is selected
            this.authenticationService.getAuthToken().subscribe((token) => {
              if (token) {
                this.headers = new HttpHeaders()
                  .set('Authorization', `Bearer ${token.AccessToken}`)
                  .set('ClientId', clientId.toString());
                obs.next(this.headers);
                obs.complete();
              }
            });
          });
        }
      } catch (err) {
        obs.error(err);
      }
    });
  }

  /**
   * Wraps a request into a new observable manually handled to
   * avoid issues with unexpected problems in the api or any
   * particular endpoints. It also adds a JWT to the request.
   * Supports automatic cancellation on route navigation (unless disabled).
   */
  public performHttpRequest<Type>(
    word: string,
    url: string,
    data: any = undefined,
    mapResult: (results: any) => any | undefined = undefined,
    validateResponse: (args: any) => unknown | undefined = undefined,
    defaultValue: Type | undefined = undefined
  ): Observable<Type> {
    const isAbsoluteUrl =
      url.startsWith('http://') || url.startsWith('https://');
    if (!isAbsoluteUrl) {
      url = this.baseUrl + url;
    }

    return new Observable<Type>((obs) => {
      // try {
      const cancel$ = this.getCancelSubject();

      this.getHttpHeaders().subscribe(() => {
        const wordsWithBody = [HTTP_WORD.POST, HTTP_WORD.PUT];
        let observableHttpCall = wordsWithBody.includes(<HTTP_WORD>word)
          ? this.http[word](url, data, { headers: this.headers })
          : this.http[word](url, { headers: this.headers });

        // Apply cancellation if enabled
        if (cancel$) {
          observableHttpCall = observableHttpCall.pipe(takeUntil(cancel$));
        }

        // Retry transient network errors (status 0, 5xx) with exponential backoff
        observableHttpCall = observableHttpCall.pipe(
          retryBeforeThrow({
            label: `MetricsApiService ${word.toUpperCase()} ${url}`,
          })
        );

        observableHttpCall.subscribe(
          (response: any) => {
            if (
              !validateResponse ||
              (validateResponse && validateResponse(response))
            ) {
              const finalResult = mapResult ? mapResult(response) : response;
              obs.next(finalResult);
              obs.complete();
            } else {
              obs.next(defaultValue);
              obs.complete();
            }
          },
          (err) => {
            this.handleApiError(obs, err, defaultValue);
          }
        );
      });
      // } catch (err) {
      //   this.handleApiError(obs, err, defaultValue);
      // }
    });
  }

  /**
   * Handles errors in hand made observables.
   * Reports to Sentry unless it's a 403 (handled by forced re-login).
   * Transient network errors (status 0) are tagged by the global beforeSend hook.
   */
  protected handleApiError(
    obs,
    exception: any = undefined,
    defaultValue: any = undefined
  ) {
    if (exception) {
      console.error(exception);
      const isForbidden =
        exception instanceof HttpErrorResponse && exception.status === 403;
      if (!isForbidden) {
        Sentry.captureException(exception);
      }

      if (
        exception instanceof HttpErrorResponse &&
        (exception.error.message.includes('Failed to fetch') ||
          exception.error.message.includes(
            'NetworkError when attempting to fetch resource'
          ) ||
          exception.error.message.includes('Load failed'))
      ) {
        console.error('http failure', exception);
        // TODO: Leaving this here until I run it past Anna. This will display
        //   an error toast if the retries fail.
        /*
        broadcastError(
          'There was a problem',
          'Check your internet connection and try again.'
        );
        */
      }
    }
    obs.next(defaultValue);
    obs.complete();
  }

  /**
   * Turns the given object into a new given type.
   */
  public mapToType<Type>(srcObj, type: new () => Type): Type {
    return Object.assign(<any>new type(), srcObj);
  }

  public post<Type>(
    url: string,
    data: any = undefined,
    mapResult: (results: any) => any | undefined = undefined,
    validateResponse: (args: any) => unknown | undefined = undefined,
    defaultValue: Type | undefined = undefined
  ): Observable<any> {
    return this.performHttpRequest<Type>(
      HTTP_WORD.POST,
      url,
      data,
      mapResult,
      validateResponse,
      defaultValue
    );
  }

  public get<Type>(
    url: string,
    mapResult: (results: any) => any | undefined = undefined,
    validateResponse: (args: any) => unknown | undefined = undefined,
    defaultValue: Type | undefined = undefined
  ): Observable<any> {
    return this.performHttpRequest<Type>(
      HTTP_WORD.GET,
      url,
      null,
      mapResult,
      validateResponse,
      defaultValue
    );
  }
}
